import { Controller } from '@hotwired/stimulus';

/**
 * Project Selection Controller
 *
 * Manages project selection within assignment modals: select-all checkbox,
 * real-time counter, and row highlighting for checked projects.
 * Works with server-side search via Turbo Frame (search is handled by
 * auto-submit controller on the form, not by this controller).
 *
 * Targets:
 * - checkbox: Each project checkbox input
 * - selectAll: The "Tout sélectionner" header checkbox
 * - count: The counter text element showing selected count
 * - row: Each project row (for highlighting)
 *
 * Actions:
 * - toggle: Called when a project checkbox changes (updates count + highlight)
 * - toggleAll: Called when select-all checkbox changes (checks/unchecks visible)
 *
 * @example
 * <div data-controller="project-selection">
 *   <input type="checkbox" data-project-selection-target="selectAll"
 *          data-action="change->project-selection#toggleAll">
 *   <label data-project-selection-target="row">
 *     <input type="checkbox" data-project-selection-target="checkbox"
 *            data-action="change->project-selection#toggle">
 *     Project name
 *   </label>
 *   <span data-project-selection-target="count">0</span>
 * </div>
 */
export default class extends Controller {
  static targets = ['checkbox', 'selectAll', 'count', 'row'];

  connect() {
    this.#updateCount();
    this.#updateSelectAll();
    this.#highlightAll();
  }

  toggle(event) {
    this.#highlightRow(event.target);
    this.#updateCount();
    this.#updateSelectAll();
  }

  toggleAll() {
    const checked = this.selectAllTarget.checked;
    this.checkboxTargets.forEach((cb) => {
      cb.checked = checked;
      this.#highlightRow(cb);
    });
    this.#updateCount();
  }

  // Private

  #updateCount() {
    if (!this.hasCountTarget) return;

    const checked = this.checkboxTargets.filter((cb) => cb.checked).length;
    this.countTarget.textContent = checked;
  }

  #updateSelectAll() {
    if (!this.hasSelectAllTarget) return;

    const total = this.checkboxTargets.length;
    const checked = this.checkboxTargets.filter((cb) => cb.checked).length;

    this.selectAllTarget.checked = total > 0 && checked === total;
    this.selectAllTarget.indeterminate = checked > 0 && checked < total;
  }

  #highlightRow(checkbox) {
    const row = checkbox.closest('[data-project-selection-target="row"]');
    if (row) {
      row.classList.toggle('bg-primary-50', checkbox.checked);
    }
  }

  #highlightAll() {
    this.checkboxTargets.forEach((cb) => this.#highlightRow(cb));
  }
}
